import Core from 'smooothy'
import gsap from 'gsap'

const DRAG_THRESHOLD = 8   // px before a press counts as a drag
const RESUME_DELAY = 1500  // ms after release before auto-scroll resumes

class AutoScrollSlider extends Core {
	#scrollSpeed = 0.15
	#hovering = false
	#pointerDown = false
	#resumeTimer = null
	#suppressClick = false
	#wasVisible = false
	#autoScroll = false
	#mq = window.matchMedia('(min-width: 1024px)')
	#reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)')
	#cleanups = []

	constructor(element, config = {}) {
		super(element, { infinite: true, snap: false, ...config })

		const sync = () => {
			this.#autoScroll = this.#mq.matches && !this.#reducedMq.matches
		}
		sync()
		this.#on(this.#mq, 'change', sync)
		this.#on(this.#reducedMq, 'change', sync)

		const originalUpdate = this.update.bind(this)
		this.boundUpdate = () => {
			const justBecameVisible = this.isVisible && !this.#wasVisible
			this.#wasVisible = this.isVisible

			if (this.#autoScroll && !this.#isPaused && this.isVisible && !justBecameVisible) {
				this.target -= this.#scrollSpeed * Math.min(this.deltaTime, 32)
			}
			originalUpdate()
		}
		gsap.ticker.add(this.boundUpdate)

		this.#setupInteraction()
	}

	// single source of truth — no more flags overwriting each other
	get #isPaused() {
		return this.#hovering || this.#pointerDown || this.#resumeTimer !== null || this.isDragging
	}

	#on(target, type, fn, opts) {
		target.addEventListener(type, fn, opts)
		this.#cleanups.push(() => target.removeEventListener(type, fn, opts))
	}

	#scheduleResume() {
		clearTimeout(this.#resumeTimer)
		this.#resumeTimer = setTimeout(() => {
			this.#resumeTimer = null
		}, RESUME_DELAY)
	}

	#setupInteraction() {
		const el = this.wrapper
		let startX = 0
		let startY = 0

		// hover pause (desktop only — pointerenter fires on touch too, so filter)
		this.#on(el, 'pointerenter', e => {
			if (e.pointerType === 'mouse') this.#hovering = true
		})
		this.#on(el, 'pointerleave', e => {
			if (e.pointerType === 'mouse') this.#hovering = false
		})

		this.#on(el, 'pointerdown', e => {
			this.#pointerDown = true
			this.#suppressClick = false
			startX = e.clientX
			startY = e.clientY
			clearTimeout(this.#resumeTimer)
			this.#resumeTimer = null
		})

		// only measures while the pointer is actually down
		this.#on(el, 'pointermove', e => {
			if (!this.#pointerDown || this.#suppressClick) return
			if (
				Math.abs(e.clientX - startX) > DRAG_THRESHOLD ||
				Math.abs(e.clientY - startY) > DRAG_THRESHOLD
			) {
				this.#suppressClick = true
			}
		})

		const endPress = () => {
			if (!this.#pointerDown) return
			this.#pointerDown = false
			this.#scheduleResume()
			// let the click event fire first, then clear the flag
			requestAnimationFrame(() => {
				this.#suppressClick = false
			})
		}
		this.#on(window, 'pointerup', endPress)
		this.#on(window, 'pointercancel', endPress)

		// one capture-phase guard instead of a listener per link
		this.#on(
			el,
			'click',
			e => {
				if (!this.#suppressClick) return
				e.preventDefault()
				e.stopPropagation()
			},
			true
		)

		// kill native image/link dragging in one delegated handler
		this.#on(el, 'dragstart', e => e.preventDefault())
	}

	destroy() {
		clearTimeout(this.#resumeTimer)
		gsap.ticker.remove(this.boundUpdate)
		this.#cleanups.forEach(fn => fn())
		this.#cleanups = []
		super.destroy?.()
	}
}

export default class FooterScroller {
	constructor() {
		const element = document.querySelector('[data-footer-scroller]')
		if (!element) return
		this.slider = new AutoScrollSlider(element)
	}

	destroy() {
		this.slider?.destroy()
	}
}